// EnquiryPage.jsx (FULL COPY-PASTE UPDATED FILE)
// ✅ Default shows TODAY created enquiries only (datePreset=today)
// ✅ Date filter added: Today / Yesterday / Last 7 Days / All / Custom Range
// ✅ Custom range uses fromDate & toDate (YYYY-MM-DD)
// ✅ Uses axiosInstance for all API calls (consistent)

import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  Loader2,
  Calendar,
  Phone,
  User,
  RefreshCw,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  ShieldCheck,
} from "lucide-react";

import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import API_BASE_URL from "../../services/api";
import axiosInstance from "../../utils/axiosInstance";

const STATUS_OPTIONS = [
  {
    value: "NEW",
    label: "New",
    color: "bg-blue-50 text-blue-700 border border-blue-100",
    icon: Clock,
  },
  {
    value: "IN_PROGRESS",
    label: "In Progress",
    color: "bg-amber-50 text-amber-700 border border-amber-100",
    icon: RefreshCw,
  },
  {
    value: "RESOLVED",
    label: "Resolved",
    color: "bg-emerald-50 text-emerald-700 border border-emerald-100",
    icon: CheckCircle,
  },
  {
    value: "CLOSED",
    label: "Closed",
    color: "bg-gray-50 text-gray-700 border border-gray-100",
    icon: ShieldCheck,
  },
];

const DEFAULT_LIMIT = 20;
const ALLOWED_LIMITS = [10, 20, 50, 100];

const SORT_OPTIONS = [
  { value: "createdAt_desc", label: "Newest First" },
  { value: "createdAt_asc", label: "Oldest First" },
  { value: "updatedAt_desc", label: "Recently Updated" },
  { value: "updatedAt_asc", label: "Least Recently Updated" },
];

function EnquiryPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // list state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // filters
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState("createdAt_desc");

  // ✅ DATE FILTER (DEFAULT = today)
  const [datePreset, setDatePreset] = useState("today"); // today | yesterday | last7 | all | custom
  const [fromDate, setFromDate] = useState(""); // YYYY-MM-DD
  const [toDate, setToDate] = useState(""); // YYYY-MM-DD

  // ui
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState("");

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: null,
    name: "",
    number: "",
    status: "NEW",
  });

  // view modal
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const handleMenuToggle = () => setSidebarOpen((s) => !s);
  const handleSidebarClose = () => setSidebarOpen(false);

  // query params (backend expects sortBy + order)
  const queryParams = useMemo(() => {
    const [sortField, sortOrder] = sortBy.split("_");

    const base = {
      page,
      limit,
      sortBy: sortField,
      order: sortOrder,
      ...(search.trim() && { search: search.trim() }),
      ...(status && { status }),
    };

    // ✅ custom range overrides preset
    if (datePreset === "custom" && fromDate && toDate) {
      return { ...base, fromDate, toDate };
    }

    // ✅ preset (DEFAULT today)
    return { ...base, datePreset };
  }, [page, limit, sortBy, search, status, datePreset, fromDate, toDate]);

  const fetchEnquiries = async (showTableLoader = true) => {
    if (showTableLoader) setTableLoading(true);
    else setLoading(true);

    setError("");

    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/api/enquiry`, {
        params: queryParams,
      });

      if (res.data?.success) {
        const { data } = res.data;
        setItems(data?.items || []);
        setTotal(data?.total || 0);
        setPages(data?.pages || 1);
      } else {
        throw new Error(res.data?.message || "Failed to fetch enquiries");
      }
    } catch (e) {
      console.error("Fetch error:", e);
      const errorMsg =
        e.response?.data?.message || e.message || "Failed to fetch enquiries";
      setError(errorMsg);
      setItems([]);
      setTotal(0);
      setPages(1);
    } finally {
      setTableLoading(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const onSearchSubmit = (e) => {
    e.preventDefault();

    // ✅ safety for custom range
    if (datePreset === "custom" && (!fromDate || !toDate)) {
      setError("Please select both From and To dates.");
      return;
    }

    setPage(1);
    fetchEnquiries();
  };

  const resetFilters = () => {
    setSearch("");
    setStatus("");
    setPage(1);
    setLimit(DEFAULT_LIMIT);
    setSortBy("createdAt_desc");

    // ✅ reset date to today (default requirement)
    setDatePreset("today");
    setFromDate("");
    setToDate("");
    setError("");
  };

  const openEdit = (row) => {
    setEditForm({
      id: row.id,
      name: row.name || "",
      number: row.number || "",
      status: row.status || "NEW",
    });
    setEditOpen(true);
  };

  const updateStatus = async (id, newStatus) => {
    setTableLoading(true);
    setError("");

    try {
      const res = await axiosInstance.patch(
        `${API_BASE_URL}/api/enquiry/${id}/status`,
        { status: newStatus }
      );

      if (res.data?.success) {
        await fetchEnquiries(false);
      } else {
        throw new Error(res.data?.message || "Failed to update status");
      }
    } catch (e) {
      console.error("Status update error:", e);
      const errorMsg =
        e.response?.data?.message || e.message || "Failed to update status";
      setError(errorMsg);
    } finally {
      setTableLoading(false);
    }
  };

  const viewEnquiry = async (id) => {
    setLoading(true);
    setError("");

    try {
      const res = await axiosInstance.get(`${API_BASE_URL}/api/enquiry/${id}`);

      if (res.data?.success) {
        setViewItem(res.data?.data || null);
        setViewOpen(true);
      } else {
        throw new Error(res.data?.message || "Failed to fetch enquiry");
      }
    } catch (e) {
      console.error("View error:", e);
      const errorMsg =
        e.response?.data?.message || e.message || "Failed to fetch enquiry";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const cfg = STATUS_OPTIONS.find((s) => s.value === status) || STATUS_OPTIONS[0];
    const IconComp = cfg.icon;

    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.color}`}
      >
        <IconComp size={10} />
        {cfg.label}
      </span>
    );
  };

  const getSortLabel = () => {
    const option = SORT_OPTIONS.find((opt) => opt.value === sortBy);
    return option ? option.label : "Newest First";
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main
        className={`pt-14 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4 md:p-5">
          <Breadcrumb />

          {/* Header */}
          <div className="mt-5 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Enquiries</h1>
                <p className="mt-0.5 text-xs text-gray-600">
                  Default shows Today created enquiries only
                </p>
              </div>

              <button
                onClick={() => fetchEnquiries()}
                disabled={loading}
                className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
                Refresh
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-5 rounded-xl bg-white p-4 shadow-sm border border-gray-200">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Filters</h2>
              <button
                onClick={resetFilters}
                className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
              >
                <X size={12} />
                Clear filters
              </button>
            </div>

            <form onSubmit={onSearchSubmit} className="space-y-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                {/* Search */}
                <div className="md:col-span-4">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Search enquiries
                  </label>
                  <div className="relative">
                    <Search
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
                      size={14}
                    />
                    <input
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                      placeholder="Search by name or number..."
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-8 pr-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Status */}
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => {
                      setStatus(e.target.value);
                      setPage(1);
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 px-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">All Status</option>
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* ✅ Date Preset */}
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Date
                  </label>
                  <select
                    value={datePreset}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDatePreset(v);
                      setPage(1);
                      if (v !== "custom") {
                        setFromDate("");
                        setToDate("");
                      }
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 px-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="all">All</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* ✅ Custom Range */}
                {datePreset === "custom" && (
                  <>
                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-700">
                        From
                      </label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => {
                          setFromDate(e.target.value);
                          setPage(1);
                        }}
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 px-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="mb-1 block text-xs font-medium text-gray-700">
                        To
                      </label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => {
                          setToDate(e.target.value);
                          setPage(1);
                        }}
                        className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 px-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </>
                )}

                {/* Sort */}
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Sort by
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setPage(1);
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 px-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Limit */}
                <div className="md:col-span-2">
                  <label className="mb-1 block text-xs font-medium text-gray-700">
                    Items per page
                  </label>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 px-3 text-xs outline-none transition-all focus:border-blue-500 focus:bg-white focus:ring-1 focus:ring-blue-500"
                  >
                    {ALLOWED_LIMITS.map((n) => (
                      <option key={n} value={n}>
                        {n} rows
                      </option>
                    ))}
                  </select>
                </div>

                {/* Apply */}
                <div className="md:col-span-2 flex items-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full rounded-lg bg-blue-600 py-2 text-xs font-medium text-white shadow-sm transition-all hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-1.5">
                        <Loader2 size={12} className="animate-spin" />
                        Applying...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-1.5">
                        <Filter size={12} />
                        Apply
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <div className="flex items-center gap-1.5 text-red-800">
                    <AlertCircle size={14} />
                    <p className="text-xs font-medium">{error}</p>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Table */}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Enquiry List</h3>
                  <p className="mt-0.5 text-xs text-gray-600">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                    <span className="mx-2">•</span>
                    Sorted by <span className="font-medium">{getSortLabel()}</span>
                    <span className="mx-2">•</span>
                    Date:{" "}
                    <span className="font-medium">
                      {datePreset === "custom" ? `${fromDate || "?"} → ${toDate || "?"}` : datePreset}
                    </span>
                  </p>
                </div>

                {tableLoading && (
                  <div className="flex items-center gap-1.5 text-xs text-gray-500">
                    <Loader2 size={12} className="animate-spin" />
                    Loading...
                  </div>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              {tableLoading ? (
                <div className="flex h-48 items-center justify-center">
                  <div className="text-center">
                    <Loader2 className="mx-auto mb-3 animate-spin text-blue-600" size={20} />
                    <p className="text-xs text-gray-600">Loading enquiries...</p>
                  </div>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">ID</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Customer</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Contact</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Updated By</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Created</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Updated</th>
                      <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 bg-white">
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-4 py-8 text-center">
                          <div className="mx-auto max-w-md">
                            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                              <Search className="text-gray-400" size={16} />
                            </div>
                            <h3 className="mb-1 text-xs font-semibold text-gray-900">
                              No enquiries found
                            </h3>
                            <p className="text-xs text-gray-600">
                              Try changing filters or selecting a different date.
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      items.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="whitespace-nowrap px-4 py-3">
                            <span className="inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800 font-mono">
                              #{row.id}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-blue-800 text-xs">
                                <User size={10} />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-gray-900 truncate max-w-[160px]">
                                  {row.name || "Unnamed"}
                                </p>
                                <p className="text-xs text-gray-500">Customer</p>
                              </div>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <Phone size={12} className="text-gray-400" />
                              <span className="text-xs font-medium text-gray-900">
                                {row.number || "N/A"}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <select
                              value={row.status}
                              onChange={(e) => updateStatus(row.id, e.target.value)}
                              className="rounded border border-gray-300 bg-transparent px-2 py-1.5 text-xs outline-none transition-all hover:border-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                              disabled={tableLoading}
                            >
                              {STATUS_OPTIONS.map((s) => (
                                <option key={s.value} value={s.value}>
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              {row.updatedBy ? (
                                <>
                                  <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-100 text-xs font-medium text-green-800">
                                    {row.updatedBy.name?.charAt(0) ||
                                      row.updatedBy.email?.charAt(0) ||
                                      "U"}
                                  </div>
                                  <span className="text-xs text-gray-700 truncate max-w-[160px]">
                                    {row.updatedBy.name || row.updatedBy.email || "-"}
                                  </span>
                                </>
                              ) : (
                                <span className="text-xs text-gray-400">Unassigned</span>
                              )}
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <Calendar size={12} className="text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {row.createdAt
                                  ? new Date(row.createdAt).toLocaleDateString()
                                  : "-"}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <Calendar size={12} className="text-gray-400" />
                              <span className="text-xs text-gray-600">
                                {row.updatedAt
                                  ? new Date(row.updatedAt).toLocaleDateString()
                                  : "-"}
                              </span>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => viewEnquiry(row.id)}
                                className="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50"
                                disabled={loading}
                                title="View details"
                              >
                                <Eye size={10} />
                                View
                              </button>

                            
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="border-t border-gray-200 px-4 py-3">
              <div className="flex flex-col items-center justify-between gap-3 sm:flex-row">
                <div className="text-xs text-gray-700">
                  Page <span className="font-semibold">{page}</span> of{" "}
                  <span className="font-semibold">{pages}</span>
                  <span className="mx-2">•</span>
                  <span className="font-semibold">{total}</span> total items
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1 || tableLoading}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeft size={12} />
                    Prev
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                      let pageNum;
                      if (pages <= 5) pageNum = i + 1;
                      else if (page <= 3) pageNum = i + 1;
                      else if (page >= pages - 2) pageNum = pages - 4 + i;
                      else pageNum = page - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setPage(pageNum)}
                          className={`h-7 w-7 rounded text-xs font-medium transition-all ${
                            page === pageNum
                              ? "bg-blue-600 text-white"
                              : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                          disabled={tableLoading}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {pages > 5 && page < pages - 2 && (
                      <>
                        <span className="px-1 text-gray-500">...</span>
                        <button
                          onClick={() => setPage(pages)}
                          className="h-7 w-7 rounded border border-gray-300 text-xs font-medium text-gray-700 hover:bg-gray-50"
                          disabled={tableLoading}
                        >
                          {pages}
                        </button>
                      </>
                    )}
                  </div>

                  <button
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                    disabled={page >= pages || tableLoading}
                    className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* View Modal */}
          {viewOpen && viewItem && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
              <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
                <div className="border-b border-gray-200 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-sm font-semibold text-gray-900">Enquiry Details</h2>
                      <p className="mt-0.5 text-xs text-gray-600">
                        Complete information about the enquiry
                      </p>
                    </div>
                    <button
                      onClick={() => setViewOpen(false)}
                      className="rounded p-1.5 hover:bg-gray-100"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <DetailCard
                      icon={<User className="text-blue-600" size={14} />}
                      label="Customer Information"
                      items={[
                        { label: "Name", value: viewItem.name || "N/A" },
                        { label: "ID", value: `#${viewItem.id}` },
                      ]}
                    />

                    <DetailCard
                      icon={<Phone className="text-green-600" size={14} />}
                      label="Contact Information"
                      items={[
                        { label: "Phone Number", value: viewItem.number || "N/A" },
                        { label: "Email", value: viewItem.email || "N/A" },
                      ]}
                    />

                    <DetailCard
                      icon={<Clock className="text-amber-600" size={14} />}
                      label="Status Information"
                      items={[
                        { label: "Current Status", value: <StatusBadge status={viewItem.status} /> },
                        {
                          label: "Last Updated By",
                          value: viewItem.updatedBy?.name || viewItem.updatedBy?.email || "N/A",
                        },
                      ]}
                    />

                    <DetailCard
                      icon={<Calendar className="text-purple-600" size={14} />}
                      label="Timestamps"
                      items={[
                        {
                          label: "Created At",
                          value: viewItem.createdAt
                            ? new Date(viewItem.createdAt).toLocaleString()
                            : "N/A",
                        },
                        {
                          label: "Last Updated",
                          value: viewItem.updatedAt
                            ? new Date(viewItem.updatedAt).toLocaleString()
                            : "N/A",
                        },
                      ]}
                    />
                  </div>

                  {viewItem.description && (
                    <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-3">
                      <h3 className="mb-2 text-xs font-semibold text-gray-900">
                        Description
                      </h3>
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">
                        {viewItem.description}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

// Helper Components
const DetailCard = ({ icon, label, items }) => (
  <div className="rounded border border-gray-200 p-3">
    <div className="mb-3 flex items-center gap-2">
      <div className="rounded bg-gray-100 p-1.5">{icon}</div>
      <h3 className="text-xs font-semibold text-gray-900">{label}</h3>
    </div>
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center justify-between gap-3">
          <span className="text-xs text-gray-600">{item.label}</span>
          <span className="text-xs font-medium text-gray-900">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export default EnquiryPage;
